import { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Padding,
  TableNoVerticalLines,
  TableNoVerticalSpaces,
  Flex
} from 'components/base/layout-components';
import Text from 'components/base/Text';
import Table from 'components/base/Table';
import { SPACES } from 'common/constants/sizes';
import styled from 'styled-components';
import { verticalScroller } from 'components/base/scroller';
import * as TrackingService from 'common/services/tracking.service';

import {
  deliveryTypeFormatter,
  getIssuesTableColumns,
  getIssuesTableData,
  getDeliveryTextsByKey
} from '../services/projectExtended.services';
import {
  getIssuesCountsV2,
  getIssuesSummaryV2,
  getIssuesV2
} from 'common/services/pdt.service';
import IterationIssuesHeader from '../components/IterationIssuesHeader';
import { TRACKING } from 'common/constants/constants';
import { DELIVERY_STATES_API_KEYS, MAX_ISSUES_KEYS } from '../projects.consts';
import HeaderForRetrospectPulseView from './HeaderForRetrospectPulseView';
import useSettingsService from 'components/hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES, SettingValues } from '@linearb/linjs-common';
import { LBButton } from '@linearb/buzz';
import useIsPulseKanbanAccumulatedDoneDisabled from './useIsPulseKanbanAccumulatedDoneDisabled';
import { getCaseInsensitive } from 'common/utils/utils';
import { useDebounceCallback } from '@react-hook/debounce';

const DEFAULT_ORDER = {
  sort_column: 'story_points',
  sort_direction: 'desc'
};

const Scroll = styled.div`
  width: 100%;
  height: 100%;
  ${verticalScroller()};
`;

const PDTIssuesTable = ({
  project,
  selectedIteration,
  selectedDeliveryState,
  setSelectedDeliveryState,
  isPulse,
  isScrumProject,
  allIterations,
  isRetrospectView = false,
  iterationSummary
}) => {
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [totals, setTotals] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [deliveryStateIssues, setDeliveryStateIssues] = useState([]);
  const [sortedIssuesKeys, setSortedIssuesKeys] = useState([]);

  const { getSettingOrDefinitionValue } = useSettingsService();

  const isVisionInsightsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isVisionInsightsEnabled
  });

  const aiServices = getSettingOrDefinitionValue({
    name: SettingValues.AI_SERVICES
  });
  const isAIServicesEnabled = aiServices?.enabled ?? false;

  const isPulseKanbanAccumulatedDoneDisabled =
    useIsPulseKanbanAccumulatedDoneDisabled();

  const fetchDataDebounced = useDebounceCallback(
    () =>
      fetchData({
        selectedIteration,
        searchValue,
        order,
        selectedDeliveryState
      }),
    400
  );

  useEffect(() => {
    setLoading(true);
    fetchDataDebounced();
  }, [selectedIteration, searchValue, order, selectedDeliveryState]);

  const getIterationProviderIds = () => {
    // in case of Done issues we want to get all issues from previous iterations
    if (
      selectedDeliveryState?.toLowerCase() === DELIVERY_STATES_API_KEYS.DONE &&
      (!isRetrospectView || !isPulseKanbanAccumulatedDoneDisabled)
    ) {
      return allIterations
        .filter(iteration => iteration.end <= selectedIteration.end)
        .map(({ provider_id }) => provider_id);
    }
    return _.get(selectedIteration, 'merged_ids', []).length > 0
      ? _.get(selectedIteration, 'merged_ids', []).map(id => id.toString())
      : [_.get(selectedIteration, 'provider_id')];
  };

  const getPayload = ({ searchValue, order, selectedDeliveryState }) => {
    return {
      search_param: searchValue,
      iteration_provider_ids: getIterationProviderIds(),
      ...order,
      project_id: project.id,
      delivery_state: selectedDeliveryState
    };
  };

  const formatTotals = totals => {
    const counts = {
      total_count: 0,
      total_story_points: 0
    };

    const totalsMap = {};
    totals.forEach(item => {
      const { status, completed, total_count, total_story_points } = item;
      const itemKey = deliveryTypeFormatter(status, completed);
      totalsMap[itemKey] = {
        total_count,
        total_story_points
      };

      // here we summing all 'planned' and 'added' issues
      if (!totalsMap[status]) {
        totalsMap[status] = { ...counts };
      }
      totalsMap[status].total_count += total_count;
      totalsMap[status].total_story_points += total_story_points;
    });

    return totalsMap;
  };

  const formatSummaryData = issues => {
    return issues.reduce((previous, current) => {
      const { delivery_type } = current; // planned_completed, added_uncompleted, Done_completed etc.
      const issueStatus = delivery_type.split('_')[0]; // planned, added, Done etc.
      const deliveryType = isScrumProject ? delivery_type : issueStatus; // counting on completed/uncompleted for scrum issues
      if (!previous[deliveryType]) {
        previous[deliveryType] = [];
      }
      previous[deliveryType].push(current);
      return previous;
    }, {});
  };

  const formatSortedIssuesKeys = totals => {
    const result = totals.map(item => {
      const { status, completed } = item;
      if (isScrumProject) return deliveryTypeFormatter(status, completed);
      return status;
    });
    setSortedIssuesKeys(result);
  };

  const fetchData = async filters => {
    setLoading(true);
    const payload = getPayload(filters);
    try {
      const [totals, summary = [], allIssues = []] = await Promise.all([
        getIssuesCountsV2({
          ...payload,
          ...(isRetrospectView &&
            isPulseKanbanAccumulatedDoneDisabled && {
              is_accumulated_done: false
            })
        }),
        !selectedDeliveryState
          ? getIssuesSummaryV2({
              ...payload,
              ...(isRetrospectView &&
                isPulseKanbanAccumulatedDoneDisabled && {
                  is_accumulated_done: false
                })
            })
          : Promise.resolve([]),
        selectedDeliveryState ? getIssuesV2(payload) : Promise.resolve([])
      ]);
      setTotals(formatTotals(totals));
      setSummaryData(formatSummaryData(summary));
      setDeliveryStateIssues(allIssues);
      formatSortedIssuesKeys(totals);
    } catch (exp) {}
    setLoading(false);
  };

  const onSort = async (colName, orderBy) => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: isPulse ? TRACKING.PAGES.PULSE : TRACKING.PAGES.PROJECTS,
      click_type: TRACKING.CLICK.PDT_ISSUES_TABLE_SORT_CLICK,
      sort_column: colName,
      sort_direction: orderBy,
      selected_delivery_type: selectedDeliveryState
    });
    setOrder({
      sort_column: colName,
      sort_direction: orderBy
    });
  };

  const onRowClick = ({ issue_provider_url }) => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: isPulse ? TRACKING.PAGES.PULSE : TRACKING.PAGES.PROJECTS,
      click_type: TRACKING.CLICK.PDT_ISSUES_TABLE_ROW_CLICK,
      issue_url: issue_provider_url
    });
    window.open(issue_provider_url);
  };

  const { issuesTableTitle } = getDeliveryTextsByKey(selectedDeliveryState);

  return (
    <Scroll id='iterations_scroll'>
      {isRetrospectView && isVisionInsightsEnabled && isAIServicesEnabled ? (
        <HeaderForRetrospectPulseView
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          iterationSummary={iterationSummary}
        />
      ) : (
        <IterationIssuesHeader
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      )}
      {loading ? (
        <Padding t='medium' l='small'>
          <Text>Loading...</Text>
        </Padding>
      ) : selectedDeliveryState ? (
        <>
          <Flex justify='center' pb='small'>
            <Flex flex={1}>
              <Text size='medium' padding={SPACES.small}>
                <LBButton
                  variant='ghost'
                  onClick={() => {
                    setSelectedDeliveryState(null);
                  }}
                >
                  &#60; Back
                </LBButton>
              </Text>
            </Flex>
            <Flex flex={2} justify='center'>
              <Text
                size='medium'
                padding={SPACES.small}
                color='shark'
                align='center'
              >
                {_.capitalize(issuesTableTitle)} (
                {getCaseInsensitive(
                  totals,
                  `${selectedDeliveryState}.total_count`,
                  0
                )}
                )
              </Text>
            </Flex>
            <Flex flex={1}></Flex>
          </Flex>
          <TableNoVerticalSpaces>
            <TableNoVerticalLines>
              <Table
                columns={getIssuesTableColumns({
                  setSelectedDeliveryState,
                  deliveryState: selectedDeliveryState,
                  numOfIssues: getCaseInsensitive(
                    totals,
                    `${selectedDeliveryState}.total_count`,
                    0
                  ),
                  numOfStorypoints: getCaseInsensitive(
                    totals,
                    `${selectedDeliveryState}.total_story_points`,
                    0
                  ),
                  isSummary: false
                })}
                data={getIssuesTableData(deliveryStateIssues)}
                sortBy={order.sort_column}
                sortOrder={order.sort_direction}
                onSort={onSort}
                onRowClick={onRowClick}
              ></Table>
            </TableNoVerticalLines>
          </TableNoVerticalSpaces>
        </>
      ) : (
        sortedIssuesKeys.map((key, idx) => {
          const rows = _.get(summaryData, key, []);
          return rows.length && idx < MAX_ISSUES_KEYS ? (
            <Padding b='medium' key={key}>
              <TableNoVerticalSpaces>
                <TableNoVerticalLines>
                  <Table
                    columns={getIssuesTableColumns({
                      setSelectedDeliveryState,
                      deliveryState: key,
                      numOfIssues: getCaseInsensitive(
                        totals,
                        `${key}.total_count`,
                        0
                      ),
                      numOfStorypoints: Math.round(
                        getCaseInsensitive(
                          totals,
                          `${key}.total_story_points`,
                          0
                        )
                      ),
                      isSummary: true,
                      isPulse
                    })}
                    data={getIssuesTableData(rows, true)}
                    onRowClick={onRowClick}
                  ></Table>
                </TableNoVerticalLines>
              </TableNoVerticalSpaces>
            </Padding>
          ) : null;
        })
      )}
    </Scroll>
  );
};

export default PDTIssuesTable;
